import { Icon } from './Icon';

/**
 * 골격 카드 (계획서 U8 · 루브릭 R3-4) — 「검색 중…」 글자만 있는 화면 대신, 결과가 놓일 자리를 같은 크기로 먼저 그린다.
 * 정사각 썸네일 · 글자 두 줄로 실제 카드와 높이가 같아 결과가 와도 배치가 흔들리지 않는다(CLS).
 * 움직임 없는 옅은 면 — 컨셉 §7 「로딩은 스피너 대신 스켈레톤」. 화면 낭독기에는 label 한 줄만 읽힌다.
 */
export function GridSkeleton({ n = 8, label, view = 'grid' }: { n?: number; label: string; view?: 'grid' | 'list' }) {
  return (
    <>
      <div className={view === 'list' ? 'res-list skel-list' : 'grid'} aria-hidden="true">
        {Array.from({ length: n }, (_, i) => (view === 'list' ? (
          <div key={i} className="res-row skel"><span className="res-row-thumb" /><div><span className="skel-line w-60" /><span className="skel-line w-40" /><span className="skel-line w-80" /></div></div>
        ) : (
          <div key={i} className="card skel"><div className="card-thumb" /><div className="card-body"><span /><span /></div></div>
        )))}
      </div>
      <span className="sr-only" role="status">{label}</span>
    </>
  );
}

/** 컬렉션 표지 카드 자리 */
export function CardsSkeleton({ n = 4, label }: { n?: number; label: string }) {
  return (
    <>
      <ul className="coll-grid" aria-hidden="true">
        {Array.from({ length: n }, (_, i) => (
          <li key={i}><div className="coll-card skel"><span className="coll-cover" /><span className="coll-card-body"><span className="skel-line w-60" /><span className="skel-line w-40" /></span></div></li>
        ))}
      </ul>
      <span className="sr-only" role="status">{label}</span>
    </>
  );
}

/**
 * 왼쪽 레일 필터 자리 — 제목 한 줄 + 줄 몇 개.
 * 14회차 m64: 768 ~ 1023 에서는 다 받은 레일이 「필터 열기」 한 줄(+ 적용된 조건 칩)로 접힌다(Facets — .facets-toggle · .facets-body).
 * 골격도 **같은 부품 · 같은 클래스**로 그려 그 폭에서는 접힌 한 줄, 1024 이상에서는 줄 골격 — 받은 뒤 목록이 위로 튀지 않는다(CLS 0.25 → 0).
 * chips 는 주소의 조건으로 미리 아는 칩 글자(받은 뒤와 같은 순서) — 칩 줄 높이까지 같게.
 */
export function RailSkeleton({ chips = [] }: { chips?: string[] }) {
  return (
    <div className="facets rail-skel-box" aria-hidden="true">
      <span className="facets-toggle btn sm skel-btn"><Icon name="filter" size={16} /> 필터{chips.length ? ` (${chips.length})` : ''} 열기</span>
      {chips.length > 0 && (
        <div className="facet-chips">
          {chips.map((l) => <span key={l} className="chip-x skel-btn">{l} <Icon name="close" size={12} /></span>)}
        </div>
      )}
      <div className="facets-body rail-skel">
        {[5, 4, 3].map((rows, g) => (
          <div key={g} className="rail-skel-group">
            <span className="skel-line w-40" />
            {Array.from({ length: rows }, (_, i) => <span key={i} className="skel-line w-80" />)}
          </div>
        ))}
      </div>
    </div>
  );
}

/** 저장한 검색 줄 자리 */
export function SavedRowsSkeleton({ label }: { label: string }) {
  return (
    <>
      <ul className="saved-list" aria-hidden="true">
        {[0, 1, 2].map((i) => <li key={i} className="saved-row skel"><div className="saved-main"><span className="skel-line w-40 h-title" /><span className="skel-line w-60" /></div></li>)}
      </ul>
      <span className="sr-only" role="status">{label}</span>
    </>
  );
}

/** 관리 화면 본문 자리 — 제목 한 줄 + 숫자 칸 + 줄 몇 개 (글자만 있는 「불러오는 중…」 대신) */
export function AdminSkeleton({ label = '관리 화면을 불러오는 중…' }: { label?: string }) {
  return (
    <div className="admin" aria-busy="true">
      <header className="admin-head"><span className="skel-line h-title w-40" aria-hidden="true" /></header>
      <div className="kpis admin-skel" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => <div key={i} className="kpi"><span className="skel-line w-60" /><span className="skel-line h-title w-40" /></div>)}
      </div>
      <div className="panel admin-skel" aria-hidden="true">
        {[80, 60, 80, 40].map((w, i) => <span key={i} className={`skel-line w-${w}`} />)}
      </div>
      <span className="sr-only" role="status">{label}</span>
    </div>
  );
}

/** 그 밖의 화면(로그인 · 가입 · 내 정보 · 비교 · 사진 검색) — 기둥 · 시작선만 같게 */
export function PageSkeleton({ label = '화면을 불러오는 중…' }: { label?: string }) {
  return (
    <div className="page offset page-skel" aria-busy="true">
      <div aria-hidden="true">
        <span className="skel-line h-title w-40" />
        <span className="skel-line w-80" />
        <span className="skel-line w-60" />
      </div>
      <span className="sr-only" role="status">{label}</span>
    </div>
  );
}

/** 표 · 목록 자리(사용자 관리 · 감사 기록) — 머리 · 도구 줄은 화면이 이미 그렸고, 줄 자리만 */
export function RowsSkeleton({ rows = 6, label }: { rows?: number; label: string }) {
  return (
    <>
      <div className="panel rows-skel" aria-hidden="true">
        {Array.from({ length: rows }, (_, i) => <span key={i} className={`skel-line w-${[80, 60, 80, 40][i % 4]}`} />)}
      </div>
      <span className="sr-only" role="status">{label}</span>
    </>
  );
}

/** 원단 상세 자리 — 목록 이동 줄 · 사진 칸 · 제목 · 요약 줄 (글자만 있는 「불러오는 중…」 대신) */
export function DetailSkeleton() {
  return (
    <div className="detail-page page page-grid" aria-busy="true">
      <div className="detail-nav page-rail" aria-hidden="true">
        <div className="detail-nav-btns">
          <span className="btn sm skel-btn">HOME</span><span className="btn sm skel-btn">PREV</span><span className="btn sm skel-btn">NEXT</span>
        </div>
      </div>
      <div className="page-main">
        <div className="detail-layout" aria-hidden="true">
          <div className="detail-left"><div className="bigimg skel-box" /></div>
          <div className="detail-right">
            <span className="skel-line h-title w-60" />
            <span className="skel-line w-40" />
            <span className="skel-line w-80" />
            <span className="skel-line w-80" />
            <span className="skel-line w-60" />
          </div>
        </div>
      </div>
      <span className="sr-only" role="status">원단 정보를 불러오는 중…</span>
    </div>
  );
}
